/**
 * Multi-Factor Authentication (MFA) Service
 * Supports Microsoft Authenticator, TOTP, SMS, and backup codes
 * Implements Essential Eight security requirements
 */

import LocalStorageService from '../storage/LocalStorageService';
import CryptoJS from 'crypto-js';

class MFAService {
  constructor() {
    this.storageKey = 'mfa_devices';
    this.backupCodesKey = 'mfa_backup_codes';
    this.encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY || 'default-key-change-in-production';
  }

  /**
   * Enable MFA for user
   */
  async enableMFA(userId, method = 'authenticator') {
    try {
      // Generate secret for TOTP
      const secret = this.generateSecret();
      
      // Create MFA device record
      const mfaDevice = {
        id: `MFA-${Date.now()}`,
        userId,
        method, // 'authenticator', 'sms', 'email'
        secret: this.encrypt(secret),
        enabled: true,
        verified: false,
        createdAt: new Date().toISOString(),
        lastUsed: null,
        backupCodes: this.generateBackupCodes()
      };
      
      // Store device
      LocalStorageService.addItem(this.storageKey, mfaDevice);
      
      // Store backup codes separately (encrypted)
      const backupCodes = {
        userId,
        codes: mfaDevice.backupCodes.map(code => this.encrypt(code)),
        used: [],
        createdAt: new Date().toISOString()
      };
      LocalStorageService.addItem(this.backupCodesKey, backupCodes);
      
      return {
        success: true,
        data: {
          secret, // Return plain secret for QR code generation
          qrCodeUrl: this.generateQRCodeUrl(userId, secret),
          backupCodes: mfaDevice.backupCodes, // Show once only
          method
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Verify MFA setup
   */
  async verifyMFASetup(userId, code) {
    try {
      const device = this.getMFADevice(userId);
      
      if (!device) {
        return { success: false, error: 'MFA device not found' };
      }
      
      // Verify code
      const isValid = this.verifyTOTPCode(this.decrypt(device.secret), code);
      
      if (isValid) {
        // Mark as verified
        device.verified = true;
        LocalStorageService.updateItem(this.storageKey, device.id, device);
        
        return { success: true, verified: true };
      }
      
      return { success: false, error: 'Invalid verification code' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Authenticate with MFA
   */
  async authenticate(userId, password, mfaCode) {
    try {
      // First verify password (would be done by auth service)
      // Then verify MFA code
      
      const device = this.getMFADevice(userId);
      
      if (!device || !device.enabled) {
        return { success: false, error: 'MFA not enabled for user' };
      }
      
      if (!device.verified) {
        return { success: false, error: 'MFA not verified. Please complete setup.' };
      }
      
      // Check if it's a backup code
      const isBackupCode = await this.verifyBackupCode(userId, mfaCode);
      
      if (isBackupCode) {
        return { success: true, method: 'backup_code' };
      }
      
      // Verify TOTP code
      const secret = this.decrypt(device.secret);
      const isValid = this.verifyTOTPCode(secret, mfaCode);
      
      if (isValid) {
        // Update last used
        device.lastUsed = new Date().toISOString();
        LocalStorageService.updateItem(this.storageKey, device.id, device);
        
        return { success: true, method: device.method };
      }
      
      return { success: false, error: 'Invalid MFA code' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate TOTP secret
   */
  generateSecret() {
    // Generate 32-character base32 secret
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return secret;
  }

  /**
   * Verify TOTP code
   */
  verifyTOTPCode(secret, code, window = 1) {
    // In production, use a proper TOTP library like 'otplib'
    // This is a simplified version
    
    const timeStep = 30; // 30 seconds
    const currentTime = Math.floor(Date.now() / 1000);
    const timeCounter = Math.floor(currentTime / timeStep);
    
    // Check current and adjacent time windows
    for (let i = -window; i <= window; i++) {
      const counter = timeCounter + i;
      const expectedCode = this.generateTOTP(secret, counter);
      
      if (expectedCode === code) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Generate TOTP code (simplified - use proper library in production)
   */
  generateTOTP(secret, counter) {
    // This is a placeholder - use 'otplib' or similar in production
    // For now, return a mock code
    const hash = CryptoJS.HmacSHA1(counter.toString(), secret).toString();
    const offset = parseInt(hash.substr(-1), 16);
    const binary = parseInt(hash.substr(offset * 2, 8), 16) & 0x7fffffff;
    return (binary % 1000000).toString().padStart(6, '0');
  }

  /**
   * Generate QR code URL for Microsoft Authenticator
   */
  generateQRCodeUrl(userId, secret) {
    const issuer = 'MedFast CRM';
    const accountName = userId;
    const otpAuthUrl = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
    
    // Use QR code service (e.g., qrcode.js or external service)
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpAuthUrl)}`;
  }

  /**
   * Generate backup codes
   */
  generateBackupCodes(count = 10) {
    const codes = [];
    for (let i = 0; i < count; i++) {
      // Generate 8-digit backup code
      const code = Math.floor(10000000 + Math.random() * 90000000).toString();
      codes.push(code);
    }
    return codes;
  }

  /**
   * Verify backup code
   */
  async verifyBackupCode(userId, code) {
    try {
      const backupCodesData = LocalStorageService.get(this.backupCodesKey, [])
        .find(bc => bc.userId === userId);
      
      if (!backupCodesData) {
        return false;
      }
      
      // Decrypt and check codes
      const decryptedCodes = backupCodesData.codes.map(c => this.decrypt(c));
      
      if (decryptedCodes.includes(code) && !backupCodesData.used.includes(code)) {
        // Mark as used
        backupCodesData.used.push(code);
        LocalStorageService.updateItem(this.backupCodesKey, backupCodesData.id, backupCodesData);
        
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get MFA device for user
   */
  getMFADevice(userId) {
    const devices = LocalStorageService.get(this.storageKey, []);
    return devices.find(d => d.userId === userId && d.enabled);
  }

  /**
   * Disable MFA for user
   */
  async disableMFA(userId) {
    try {
      const device = this.getMFADevice(userId);
      
      if (device) {
        device.enabled = false;
        LocalStorageService.updateItem(this.storageKey, device.id, device);
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Regenerate backup codes
   */
  async regenerateBackupCodes(userId) {
    try {
      const newCodes = this.generateBackupCodes();
      
      const backupCodesData = LocalStorageService.get(this.backupCodesKey, [])
        .find(bc => bc.userId === userId);
      
      if (backupCodesData) {
        backupCodesData.codes = newCodes.map(code => this.encrypt(code));
        backupCodesData.used = [];
        backupCodesData.createdAt = new Date().toISOString();
        LocalStorageService.updateItem(this.backupCodesKey, backupCodesData.id, backupCodesData);
      } else {
        const newBackupCodes = {
          userId,
          codes: newCodes.map(code => this.encrypt(code)),
          used: [],
          createdAt: new Date().toISOString()
        };
        LocalStorageService.addItem(this.backupCodesKey, newBackupCodes);
      }
      
      return {
        success: true,
        backupCodes: newCodes // Show once only
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if MFA is required for user
   */
  isMFARequired(userId) {
    const device = this.getMFADevice(userId);
    return device && device.enabled && device.verified;
  }

  /**
   * Encrypt sensitive data
   */
  encrypt(data) {
    return CryptoJS.AES.encrypt(data, this.encryptionKey).toString();
  }

  /**
   * Decrypt sensitive data
   */
  decrypt(encryptedData) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Decryption error:', error);
      return null;
    }
  }

  /**
   * Check MFA status for user
   */
  getMFAStatus(userId) {
    const device = this.getMFADevice(userId);
    
    if (!device) {
      return {
        enabled: false,
        verified: false,
        method: null
      };
    }
    
    return {
      enabled: device.enabled,
      verified: device.verified,
      method: device.method,
      lastUsed: device.lastUsed
    };
  }
}

export default new MFAService();

